import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../api';

// One native <dialog> and one toast for the whole console. Dialog bodies are uncontrolled form fields;
// on submit the values are collected with FormData (names ending in [] become arrays).
type Values = Record<string, any>;
type DialogSpec = { title: string; okLabel?: string; body: ReactNode; onSubmit?: (v: Values, form: HTMLFormElement) => Promise<void> | void; hideCancel?: boolean };
type Ctx = { openDialog: (d: DialogSpec) => void; closeDialog: () => void; toast: (msg: string, isError?: boolean) => void };
const FeedbackCtx = createContext<Ctx | null>(null);

export const useFeedback = () => { const c = useContext(FeedbackCtx); if (!c) throw new Error('no FeedbackProvider'); return c; };

export const errorText = (err: unknown) => {
  if (err instanceof ApiError && err.detail?.legal_moves) return `${err.message} — legal: ${err.detail.legal_moves.map((m) => m.to_name).join(', ')}`;
  return err instanceof Error ? err.message : String(err);
};

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [spec, setSpec] = useState<DialogSpec | null>(null);
  const [key, setKey] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toastState, setToast] = useState<{ msg: string; err: boolean; show: boolean }>({ msg: '', err: false, show: false });
  const timer = useRef<number>(undefined);

  const toast = useCallback((msg: string, isError = false) => {
    setToast({ msg, err: isError, show: true });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), isError ? 6000 : 2800);
  }, []);
  const openDialog = useCallback((d: DialogSpec) => { setSpec(d); setError(''); setKey((k) => k + 1); }, []);
  const closeDialog = useCallback(() => ref.current?.close(), []);

  useEffect(() => {
    const dlg = ref.current;
    if (!spec || !dlg) return;
    if (!dlg.open) dlg.showModal();
    const first = dlg.querySelector<HTMLElement>('.dlg-body input:not([type=hidden]):not([type=radio]):not([type=checkbox]), .dlg-body select, .dlg-body textarea, .dlg-body input[type=radio]:checked');
    first?.focus();
  }, [spec, key]);

  // Light dismiss for browsers without closedby="any".
  useEffect(() => {
    const dlg = ref.current;
    if (!dlg || 'closedBy' in dlg) return;
    const on = (ev: MouseEvent) => { const r = dlg.getBoundingClientRect(); if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) dlg.close(); };
    dlg.addEventListener('click', on);
    return () => dlg.removeEventListener('click', on);
  }, []);

  const submit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (!spec?.onSubmit) return closeDialog();
    const values: Values = {};
    for (const [k, v] of new FormData(ev.currentTarget).entries()) {
      if (k.endsWith('[]')) (values[k.slice(0, -2)] ||= []).push(v); else values[k] = v;
    }
    setBusy(true); setError('');
    try { await spec.onSubmit(values, ev.currentTarget); closeDialog(); }
    catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };

  return (
    <FeedbackCtx.Provider value={{ openDialog, closeDialog, toast }}>
      {children}
      <dialog ref={ref} closedby="any" aria-labelledby="dlg-title" onClose={() => setSpec(null)}>
        {spec && (
          <form key={key} onSubmit={submit}>
            <h2 id="dlg-title">{spec.title}</h2>
            <div className="dlg-body">{spec.body}</div>
            <div className="error" role="alert">{error}</div>
            <div className="actions">
              {!spec.hideCancel && <button type="button" className="quiet" onClick={closeDialog}>Cancel</button>}
              <button type="submit" disabled={busy}>{spec.okLabel ?? 'Save'}</button>
            </div>
          </form>
        )}
      </dialog>
      <div id="toast" role="status" className={(toastState.show ? 'show' : '') + (toastState.err ? ' err' : '')}>{toastState.msg}</div>
    </FeedbackCtx.Provider>
  );
}
